/**
 * UI/UX-Audit (#556): Tastatur-Führung für alle Fenster an einer Stelle.
 *
 * Vorher hatte kein Fenster eine Fokus-Falle: Tab lief hinter das Fenster in die Seite, Screenreader wussten nicht,
 * dass ein Fenster offen ist. Statt ~90 Fenster einzeln umzubauen, markiert jedes seinen Inhalt mit
 * `role="dialog" aria-modal="true" data-fenster` — der Rest passiert hier:
 *  - beim Erscheinen: Fokus ins Fenster (außer etwas darin hat ihn schon, z. B. autoFocus), Beschriftung aus der
 *    ersten Überschrift, falls keine gesetzt ist;
 *  - Tab/Umschalt+Tab bleiben im OBERSTEN Fenster;
 *  - beim Verschwinden: Fokus zurück dorthin, wo er vorher war.
 * Esc und Klick daneben bleiben Sache des Fensters (die meisten schließen schon so; doppelt wäre falsch).
 */
const FOKUSSIERBAR = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';

interface Eintrag { el: HTMLElement; vorher: Element | null }
const stapel: Eintrag[] = [];
let naechsteId = 1;

const sichtbar = (el: HTMLElement) => el.getClientRects().length > 0 || el === document.activeElement;
const fokussierbare = (el: HTMLElement) =>
  Array.from(el.querySelectorAll<HTMLElement>(FOKUSSIERBAR)).filter(e => !e.closest('[inert]') && sichtbar(e));

const beschrifte = (el: HTMLElement) => {
  if (el.getAttribute('aria-label') || el.getAttribute('aria-labelledby')) return;
  const titel = el.querySelector<HTMLElement>('h1, h2, h3');
  if (!titel) return;
  if (!titel.id) titel.id = `fenster-titel-${naechsteId++}`;
  el.setAttribute('aria-labelledby', titel.id);
};

const oeffne = (el: HTMLElement) => {
  if (stapel.some(e => e.el === el)) return;
  stapel.push({ el, vorher: document.activeElement });
  beschrifte(el);
  if (el.contains(document.activeElement)) return;
  const erstes = fokussierbare(el)[0];
  if (erstes) erstes.focus({ preventScroll: true });
  else { if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '-1'); el.focus({ preventScroll: true }); }
};

const schliesse = (el: HTMLElement) => {
  const i = stapel.findIndex(e => e.el === el);
  if (i < 0) return;
  const [{ vorher }] = stapel.splice(i, 1);
  // Nur zurückgeben, wenn der Fokus mit dem Fenster verschwunden ist — nicht, wenn schon ein anderes Fenster ihn hat
  const weg = !document.activeElement || document.activeElement === document.body || !document.activeElement.isConnected;
  if (weg && vorher instanceof HTMLElement && vorher.isConnected) vorher.focus({ preventScroll: true });
};

const fensterIn = (knoten: Node): HTMLElement[] => {
  if (!(knoten instanceof HTMLElement)) return [];
  const liste = Array.from(knoten.querySelectorAll<HTMLElement>('[data-fenster]'));
  return knoten.hasAttribute('data-fenster') ? [knoten, ...liste] : liste;
};

const tab = (e: KeyboardEvent) => {
  if (e.key !== 'Tab') return;
  while (stapel.length && !stapel[stapel.length - 1].el.isConnected) stapel.pop();
  const oben = stapel[stapel.length - 1]?.el;
  if (!oben) return;
  const ziele = fokussierbare(oben);
  if (ziele.length === 0) { e.preventDefault(); oben.focus(); return; }
  const erstes = ziele[0];
  const letztes = ziele[ziele.length - 1];
  const aktiv = document.activeElement;
  if (!oben.contains(aktiv)) { e.preventDefault(); (e.shiftKey ? letztes : erstes).focus(); return; }
  if (e.shiftKey && (aktiv === erstes || aktiv === oben)) { e.preventDefault(); letztes.focus(); }
  else if (!e.shiftKey && aktiv === letztes) { e.preventDefault(); erstes.focus(); }
};

let aktiv = false;
/** Einmal beim Start (index.tsx). Mehrfacher Aufruf schadet nicht. */
export const starteFensterFokus = (wurzel: HTMLElement = document.body): (() => void) => {
  if (aktiv) return () => undefined;
  aktiv = true;
  const beobachter = new MutationObserver(aenderungen => {
    for (const a of aenderungen) {
      a.removedNodes.forEach(n => fensterIn(n).forEach(schliesse));
      a.addedNodes.forEach(n => fensterIn(n).forEach(oeffne));
    }
  });
  beobachter.observe(wurzel, { childList: true, subtree: true });
  fensterIn(wurzel).forEach(oeffne);
  document.addEventListener('keydown', tab, true);
  return () => { beobachter.disconnect(); document.removeEventListener('keydown', tab, true); stapel.length = 0; aktiv = false; };
};
